import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Star, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Partners = () => {
  const { t } = useLanguage();

  const partners = [
    { 
      name: 'Club Med', 
      locations: ['Marina Smir', 'Marrakech'],
      type: 'Resort',
      logo: 'CM'
    },
    { 
      name: 'Palmariva Resorts', 
      locations: ['Marrakech'],
      type: 'Resort',
      logo: 'PR'
    },
    { 
      name: 'Événements Privés', 
      locations: ['Mariages', 'Galas'],
      type: 'Events',
      logo: 'EP'
    }
  ];

  const targetPartners = [
    { name: 'Resorts Balnéaires', description: 'Activités familiales' },
    { name: 'Hôtellerie de Plein Air', description: 'Campings de luxe' },
    { name: 'Événementiel MICE', description: 'Séminaires & Galas' }
  ];

  return (
    <section 
      id="partners" 
      data-testid="partners-section"
      className="py-24 md:py-32 relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#050505]" />
      <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-[#FF7A00]/5 rounded-full blur-[200px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-[#FF7A00]/10 text-[#FF7A00] text-sm tracking-widest uppercase mb-4">
            Partners
          </span>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
            {t('partners.title')}
          </h2>
          <p className="text-lg text-white/60 max-w-xl mx-auto">
            {t('partners.subtitle')}
          </p>
        </motion.div>

        {/* Partners Marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20 overflow-hidden"
        >
          <div className="flex gap-6 animate-marquee">
            {[...partners, ...partners, ...partners].map((partner, index) => (
              <div
                key={index}
                data-testid={`partner-card-${index % partners.length}`}
                className="flex-shrink-0 glass-card rounded-2xl p-6 w-[280px] hover:border-[#FF7A00]/30 transition-colors"
              >
                <div className="flex items-center gap-4 mb-4">
                  {/* Logo Placeholder */}
                  <div className="w-14 h-14 rounded-xl bg-[#FF7A00]/10 border border-[#FF7A00]/20 flex items-center justify-center">
                    <span className="text-lg font-bold text-[#FF7A00]">{partner.logo}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">{partner.name}</h4>
                    <span className="text-xs text-[#FF7A00]">{partner.type}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {partner.locations.map((loc, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 text-xs text-white/60">
                      <MapPin size={10} />
                      {loc}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Target Partners */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Partenaires Cibles</h3>
          <p className="text-white/60">Solutions adaptées à chaque secteur</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {targetPartners.map((target, index) => (
            <div
              key={index}
              data-testid={`target-partner-${index}`}
              className="glass-card rounded-2xl p-6 text-center hover:border-[#FF7A00]/30 transition-colors group"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF7A00]/10 border border-[#FF7A00]/20 flex items-center justify-center group-hover:bg-[#FF7A00]/20 transition-colors">
                <Building2 className="w-7 h-7 text-[#FF7A00]" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">{target.name}</h4>
              <p className="text-sm text-white/50">{target.description}</p>
            </div>
          ))}
        </motion.div>

        {/* Expansion Note */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[#FF7A00]/10 border border-[#FF7A00]/20">
            <Star className="w-5 h-5 text-[#FF7A00]" />
            <span className="text-white/80">
              <span className="font-semibold text-[#FF7A00]">Expansion:</span> Déploiements à venir dans plusieurs chaînes d'hôtels internationales
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Partners;
